import React, { Component, useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { storageKeys } from 'settings';
import { APIHandler, DataHandler, StorageHandler, ContentHeader, BadgeContainer, Badge } from 'C4';
import BadgeDisplay from 'components/BadgeDisplay';
import usePointsHandler from 'hooks/usePointsHandler';
import badges from 'assets/images/badges';

import Progress from 'components/Progress';

import i18n from 'i18n-react';
import Diploma from 'components/Diploma';
import useSettings from 'hooks/useSettings';
import Modal from 'components/Modal';
import ShareBox from 'components/ShareBox';
import RewardMenu from 'components/RewardMenu';

/**
 * BadgeView
 *
 * A view component that displays each attained badge for the user. These badges being:
 * - Bronze/Silver/Gold/Platinum badges: Based on the amount of completed trainings
 * - Specialization/Province badges: Based on completing specific specialization training chapters
 * - Specialization/Province completion: Obtained when completing both the main chapter and at least 1 city subchapter for that province.
 *
 *
 * @returns {element} : A view component
 */
const BadgeView = () => {
  const { tierBadges, alternateTierBadges } = badges;
  const tierIndex = tiers && Math.min(getTieredValue(), tiers.length - 1);

  const [user, setUser] = useState({
    firstName: '',
    lastName: '',
    email: '',
    agencyName: '',
  });

  const [language, setLanguage] = useState('US');

  const [swal, setSwal] = useState([]);

  const [popoutMetalBadges, setPopoutMetalBadges] = useState(alternateTierBadges);

  const [showDiploma, setShowDiploma] = useState(false);
  const [showBadgePopout, setShowBadgePopout] = useState(false);

  const [selectedBadge, setSelectedBadge] = useState({ url: '', title: '' });

  const { appId } = useSettings();

  const [specializationProgress, setSpecializationProgress] = useState({});

  const { isLoading, tiers, totalPoints, currentTierIndex, completedItems } = usePointsHandler();

  const handleDiplomaOpen = () => setShowDiploma(true);
  const handleDiplomaClose = () => setShowDiploma(false);

  const handleBadgeOpen = (index) => {
    setSelectedBadge({
      url: popoutMetalBadges[index] || tierBadges[index],
      title: tiers[index]?.title || '',
    });
    setShowBadgePopout(true);
  };
  const handleBadgeClose = () => setShowBadgePopout(false);

  const [diploma, setDiploma] = useState({
    status: false,
    imgUrl: '',
  });

  const [provinces, setProvinces] = useState([]);

  // triggers call to update user info on mount
  useEffect(() => {
    getUserInfo();
  }, []);

  useEffect(() => {
    getPopoutMetalBadges(user.firstName, user.lastName);
  }, [user.firstName, user.lastName]);

  // Triggers call to update training progress on user.email change.
  useEffect(() => {
    if (!(user.email === '')) getTrainingProgress(user);
  }, [user.email]);

  // Triggers call to update training progress on user.email change. Seperate from trainingProgress in case of (future) divergent triggers
  useEffect(() => {
    if (!(user.email === '')) getSpecializationsAndProgress(user);
  }, [user.email]);

  /**
   * Gets the user information from storage
   */
  const getUserInfo = async () => {
    const userRes = await StorageHandler.load(storageKeys.user);
    const lang = await StorageHandler.load(storageKeys.lang);
    if (lang) {
      setLanguage(lang);
    }

    const { email, first_name: firstName, last_name: lastName, agency_name: agencyName } = userRes;

    setUser((prevState) => {
      return {
        ...prevState,
        email,
        firstName,
        lastName,
        agencyName,
      };
    });
  };

  /**
   * getPopoutMetalBadges
   *
   * A function that returns an array of image urls for the popout badges (incl. name params), overwriting the default unnamed ones.
   * 'none' is a placeholder for when no alternate badge is available (as is the case for bronze)
   *
   * @returns {array} An array of popout badge image urls
   */
  const getPopoutMetalBadges = (firstName, lastName) => {
    if (firstName && lastName) {
      const bronzeBadgeUrl = 'none';
      const silverBadgeUrl = `https://crm.travpromobile.com/api/view-diploma?first_name=${firstName}&last_name=${lastName}&app_id=CSP_SilverBadge`;
      const goldBadgeUrl = `https://crm.travpromobile.com/api/view-diploma?first_name=${firstName}&last_name=${lastName}&app_id=CSP_GoldBadge`;
      const platinumBadgeUrl = `https://crm.travpromobile.com/api/view-diploma?first_name=${firstName}&last_name=${lastName}&app_id=CSP_PlatinumBadge`;

      setPopoutMetalBadges(() => {
        return [bronzeBadgeUrl, silverBadgeUrl, goldBadgeUrl, platinumBadgeUrl];
      });
    }
  };

  /**
   * chaptersComplete calculates whether passed chapters are completed or not.
   * @param {array} chapters An array of chapters. Chapter requires the .total_chapters and .progress properties
   * @param {number} slack Optional: Permitted slack when determining chapter completion. Default: 0
   * @returns {bool} true when chapters exist && they are completed.
   */
  const chaptersComplete = (chapters, slack = 0) => {
    if (chapters.length === 0) return false;

    for (const chapter of chapters) {
      const { total_pages: totalPages, progress } = chapter;
      if (totalPages - progress > slack) return false;
    }
    return true;
  };

  const cleanUrl = (url) => {
    const devprefix = 's3.amazonaws.com/developertool';
    const newprefix = 'front.travpromobile.com';
    return url.replace(devprefix, newprefix);
  };

  /**
   *  provinceCompleted
   *
   * Determines if an entered province object is completed according to entered progress.
   * Completion is defined as one of the following:
   *  - Having completed the 'main' province session AND having completed 1 or more 'sub' city sessions.
   *  - Having completed the 'main' province session AND there being no 'sub' city sessions for that province: I.e. Only one session exists.
   *
   * @param {Object} Province Requires a province/subApp, with a number of sessions (1 province + n=>1 cities)
   * @param {Object} Progress Requires an object with the collected progress.
   */
  const provinceCompleted = (province, progress) => {
    let hasCompletedMain = false;
    let hasCompletedSub = false;
    const { sessions } = province;

    sessions.forEach((session) => {
      const { type, sessionAppId } = session;
      const status = Boolean(progress[sessionAppId]?.status);
      if (type === 'main' && !hasCompletedMain) hasCompletedMain = status;
      if (type === 'sub' && !hasCompletedSub) hasCompletedSub = status;
    });
    return hasCompletedMain && (hasCompletedSub || sessions.length === 1);
  };

  /**
   * getSpecializationsAndProgress
   *
   * A function that sets the states based on specialization/province progress:
   *  - Badges.Gold (based number of completed specializations >= 10)
   *  - Badges.Platinum (based number of completed specializations >= 12)
   *  - ProvinceProgress (based on specific completed chapters)
   *
   * @param {string} email The user email
   */
  const getSpecializationsAndProgress = async (user) => {
    const { email } = user;

    const appId = await DataHandler.matchAppId();

    const subAppRes = await APIHandler.getSubApps(appId);

    let subAppIds = [];
    let subApps = [];
    let completedProvincesCount = 0;
    const goldProvincesRequired = 10;
    const platinumProvincesRequired = 12;

    // Iterate through all collected subApps from response, organize relevant info and save to Provinces state
    subAppRes.forEach((subApp) => {
      let app = {
        title: subApp.title,
        sessions: [],
      };
      subApp.sessions.forEach((session) => {
        subAppIds.push(session.session_app_id);
        app.sessions.push({
          title: session.title,
          sessionAppId: session.session_app_id,
          sessionChapterId: session.session_chapter_id,
          type: session.type,
        });
      });
      subApps.push(app);
    });
    setProvinces(subApps);

    const progressRes = await APIHandler.getUserProgress(subAppIds, email);

    // Iterate through each progress datapoint from response, organize and check completion status and update to const/state.
    const progress = {};
    if (progressRes.status === 'success') {
      progressRes.data.forEach((specialization) => {
        const { chapters, badge } = specialization;
        progress[specialization.app_id] = {
          status: chaptersComplete(chapters, 1),
          imgUrl: cleanUrl(badge),
        };
      });
    }

    setSpecializationProgress(() => {
      return progress;
    });

    // Combine collected data to check for the number of completed subApps
    subApps.forEach((subApp) => {
      if (provinceCompleted(subApp, progress)) {
        completedProvincesCount += 1;
      }
    });
  };

  /**
   * A function that sets the states based on foundational and experience progress:
   *  - Badges.bronze (based specific completed basic chapters: 1-4)
   *  - Badges.silver (based specific completed basic chapters >= 5-10)
   *  - ProvinceProgress (based on specific completed chapters)
   */
  const getTrainingProgress = async (user) => {
    const { firstName, lastName, email } = user;

    const appId = await DataHandler.matchAppId();
    const progressRes = await DataHandler.checkKey(storageKeys.progress, APIHandler.getUserProgress(appId, email));

    if (progressRes.status === 'success') {
      const chapters = progressRes?.data[0]?.chapters || [];

      const bronzeChapters = chapters.slice(0, 4);
      const silverChapters = chapters.slice(4);

      const bronzeStatus = chaptersComplete(bronzeChapters, 1);
      const silverStatus = chaptersComplete(silverChapters, 1);
      const diplomaStatus = bronzeStatus && silverStatus;

      const diplomaUrl = `https://cms.travpromobile.com/api/app/diploma?first_name=${firstName}&last_name=${lastName}&app_id=${appId}`;

      setDiploma((prevState) => {
        return {
          ...prevState,
          status: diplomaStatus,
          imgUrl: diplomaUrl,
        };
      });
    }
  };

  // Creates an array of BadgeContainers, sorted by completion status.
  const renderBadgeContainers = useMemo(() => {
    const completeBadges = [];
    const incompleteBadges = [];

    provinces.forEach((province) => {
      const { title: provinceTitle, sessions } = province;
      // const provinceStatus = provinceCompleted(province, specializationProgress)

      sessions
        .filter((session) => {
          const { sessionAppId } = session;
          const imgUrl = specializationProgress[sessionAppId]?.imgUrl;

          if (imgUrl) return true;
        })
        .forEach((session) => {
          const { title: sessionTitle, sessionAppId, sessionChapterId, type } = session;
          const status = specializationProgress[sessionAppId]?.status;
          const imgUrl = specializationProgress[sessionAppId]?.imgUrl || '';

          // lockLinkUrl params
          const currentAppUrl = window.location.origin;
          const currentPath = window.location.pathname;
          const { email } = user;
          const urlToTraining = `https://academy.travpromobile.com/training/${sessionAppId}/${email}/${sessionChapterId}/0/true/true/?redirurl=${currentAppUrl}${currentPath}`;

          if (status) {
            completeBadges.push(
              <Badge
                key={sessionAppId}
                badgeImg={imgUrl}
                status={status}
                type={type}
                lockLinkUrl={urlToTraining}
                onClick={() => {
                  setSelectedBadge({
                    url: imgUrl,
                    title: sessionTitle,
                  });
                  setShowBadgePopout(true);
                }}
              />,
            );
          } else {
            incompleteBadges.push(
              <Badge key={sessionAppId} badgeImg={imgUrl} status={status} type={type} lockLinkUrl={urlToTraining} />,
            );
          }
        });
    });

    return (
      <React.Fragment>
        {completeBadges.length > 0 && (
          <BadgeContainer
            key="badge-container-complete"
            showLine
            title={i18n.translate('badgeView.badgesCompleteTitle')}
            subtitle={i18n.translate('badgeView.badgesCompleteSubtitle')}
          >
            {completeBadges}
          </BadgeContainer>
        )}
        {incompleteBadges.length > 0 && (
          <BadgeContainer
            key="badge-container-incomplete"
            showLine
            // title={''}
            subtitle={i18n.translate('badgeView.badgesIncompleteSubtitle')}
          >
            {incompleteBadges}
          </BadgeContainer>
        )}
      </React.Fragment>
    );
  }, [specializationProgress, provinces]);

  const { diplomaProgress } = usePointsHandler();
  const currentPath = window.location.pathname;

  // will check if the subtitle should include the rewards link or not, and sets the subtitle accordingly
  const selectedPointsBarSubtitle = useMemo(() => {
    if (!currentTierIndex) return '';

    const rank = capitalizeWords(tiers[currentTierIndex].title);

    if (currentTierIndex === tiers?.length - 1) {
      // Final tier: include the rewards link (default US)
      const urls = {
        au: 'https://travpromobile-my.sharepoint.com/:w:/p/ansley/EUTf9CaFLCdPjUfqiahTLPQBT90yncM7yTFK1AtsEFGV4Q?e=O8wB3d',
        nz: 'https://travpromobile-my.sharepoint.com/:w:/p/ansley/EUTf9CaFLCdPjUfqiahTLPQBT90yncM7yTFK1AtsEFGV4Q?e=O8wB3d',
        gb: 'https://travpromobile-my.sharepoint.com/:w:/p/ansley/EUTf9CaFLCdPjUfqiahTLPQBT90yncM7yTFK1AtsEFGV4Q?e=O8wB3d',
        ie: 'https://travpromobile-my.sharepoint.com/:w:/p/ansley/EUTf9CaFLCdPjUfqiahTLPQBT90yncM7yTFK1AtsEFGV4Q?e=O8wB3d',
        mx: 'https://ctccct.sharepoint.com/:b:/s/CanadaSpecialistProgramCSP/EWzFNDO8Qt5Kl_XHBLCaoBoBFHs8413mq7MAnYMj6sPx3g?e=ILfdTB',
        kr: 'https://ctccct.sharepoint.com/:b:/s/CanadaSpecialistProgramCSP/EZD0wAjWZ4lCv_Dxf9upDfkB30NhJ_2So8gPb_LVG_RkRA?e=Ds9neD',
        jp: 'https://ctccct.sharepoint.com/:b:/s/CanadaSpecialistProgramCSP/EaBlejAixW9Ntww1UrES7qIB_QQFhYwVZj_u5rkBM3N4Tg?e=Rx8NUU',
        de: 'https://ctccct.sharepoint.com/:b:/s/CanadaSpecialistProgramCSP/Ef7BdtO5q_hFtqqqGII6vNQBUS7lnY-aFl8mrYajiNmQYA?e=9eId1u',
        cafr: 'https://front.travpromobile.com/787/2023-11-03_Final CSP Rewards Document (Oct 17)_CG_SL_FR (003) - FR.pdf',
        fr: 'https://front.travpromobile.com/787/2023-11-03_Final CSP Rewards Document (Oct 17)_CG_SL_FR (003) - FR.pdf',
        default:
          'https://travpromobile-my.sharepoint.com/:w:/p/ansley/EUTf9CaFLCdPjUfqiahTLPQBT90yncM7yTFK1AtsEFGV4Q?e=O8wB3d',
      };

      let languageCode = language.code;
      const destinationUrl = urls[languageCode.toLowerCase()] || urls['default'];

      return (
        <React.Fragment>
          {i18n.translate('badgeView.badgeAchievementWithRewards.start', { rank })}{' '}
          <a className="badgeView__rewards-link" href={destinationUrl} target="_blank" rel="noopener noreferrer">
            {i18n.translate('badgeView.badgeAchievementWithRewards.link')}
          </a>{' '}
          {i18n.translate('badgeView.badgeAchievementWithRewards.end', { rank })}
        </React.Fragment>
      );
    }

    // Other tiers: return subtitle without the link
    return <React.Fragment>{i18n.translate('badgeView.badgeAchievement', { rank })}</React.Fragment>;
  }, [tiers, currentTierIndex]);

  return (
    <div className="contentContainer badgeView">
      {currentPath === '/main/rewards/achievements' && <RewardMenu />}

      <Modal show={showBadgePopout} onHide={handleBadgeClose} bgClose>
        {/* FIXME: TEMP Props */}
        <ShareBox
          image={selectedBadge.url}
          url={selectedBadge.url}
          title={selectedBadge.title}
          subject={i18n.translate('sharing.shareSubjectBadge')}
          body={i18n.translate('sharing.shareBodyBadge', { chapterTitle: selectedBadge.title })}
          email
          download
          size="sm"
        />
      </Modal>

      <ContentHeader header={i18n.translate('badgeView.title')} subtitle={i18n.translate('badgeView.subtitle')} />

      <div className="content-box">
        {!isLoading && diplomaProgress !== 0 && (
          <div className="badgeContainer points__container">
            <h1 className="badgeView__title--badge-display">{i18n.translate('badgeView.badgeDisplayTitle')}</h1>
            <BadgeDisplay
              tierBadges={tierBadges}
              popoutBadges={popoutMetalBadges}
              tierIndex={currentTierIndex}
              tiers={tiers}
              totalPoints={totalPoints}
              badgeSubtitleOverride={''}
              pointsBarSubtitle={selectedPointsBarSubtitle}
              layout="transparantBar"
              hideBadge
              hasPopout
            />
            {/* Renders the tiers icons and their completion status */}
            <div className="badgeView__title--tiers">
              {tiers &&
                tiers.length &&
                tiers.map((tier, index) => (
                  <div
                    key={tier.title}
                    className={`tier-title ${tierIndex === index ? 'active' : ''} ${tier.completedItems >= tier.count ? '' : 'disabled'
                      }`}
                    onClick={() => {
                      if (popoutMetalBadges[index] !== 'none') {
                        handleBadgeOpen(index);
                      }
                    }}
                  >
                    <img className="badge" src={badges.tierBadges[index]} alt="Logo" />

                    <span className="title">
                      {Math.min(tier.completedItems, tier.count)}
                      {' / '}
                      {tier.count} {i18n.translate('badgeView.complete')}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        )}

        {diplomaProgress >= 100 && (
          <div>
            <div className="diploma__container" onClick={handleDiplomaOpen}>
              <img className="diploma__image" src={diploma.imgUrl} alt="diploma" />
            </div>
            <Modal show={showDiploma} onHide={handleDiplomaClose} bgClose>
              <Diploma appId={appId} firstName={user.firstName} lastName={user.lastName} email={user.email} />
            </Modal>
          </div>
        )}

        <div className="content-box__row">
          <div className="badge-view__line" />
        </div>

        {renderBadgeContainers}
      </div>
    </div>
  );
};

export default BadgeView;

function capitalizeWords(str) {
  return str
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}
